import React, {
  useState, useCallback, useEffect, useLayoutEffect, useRef,
} from 'react'
import type { TarotPhase } from './tarotTheaterMachine'
import { getCardDimensions } from '../hooks/getCardDimensions'
import { REVEAL_OVERLAP_MS } from './tarotStageGeometry'
import { TarotCardStage } from './TarotCardStage'

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

export interface TarotTheaterProps {
  phase: TarotPhase
  frontImageUrl?: string
  shuffleElapsedMs: number
  backImageUrl?: string
  contentAreaRef?: React.RefObject<HTMLDivElement | null>
  /** Fired by the stage at the pick→fly boundary → host hook.chooseCard */
  onChooseCard: () => void
  /** Wired to the flip animation's onComplete for the flip→land flight → host hook.flipEnd */
  onFlipEnd: () => void
}

export const TarotTheater: React.FC<TarotTheaterProps> = ({
  phase,
  frontImageUrl,
  backImageUrl,
  contentAreaRef,
  onChooseCard,
  onFlipEnd,
}) => {
  const [capturedRect, setCapturedRect] = useState<Rect | null>(null)
  // Reveal overlap (AI-823): hold the flip overlay opaque on the landing for
  // REVEAL_OVERLAP_MS AFTER the FSM advances land → tarot_idle, so the
  // already-decoded front image covers the structured card's fade-in (its own
  // <img> decodes from `--loading`), then unmount invisibly once the card is
  // solid. `revealHolding` (state) carries the hold once armed; `enteringReveal`
  // (computed below) detects the land → tarot_idle edge SYNCHRONOUSLY during
  // render so the stage is never unmounted for a frame before the state arms —
  // an unmount would blank a frame AND drop the stage's decoded-image/landing
  // state on remount, defeating the whole overlap.
  const [revealHolding, setRevealHolding] = useState(false)
  // prevPhaseRef is updated only in the effect below (post-paint), so it still
  // reads the pre-transition phase during the render that first sees tarot_idle.
  const prevPhaseRef = useRef<TarotPhase>(phase)
  const enteringReveal = phase === 'tarot_idle' && prevPhaseRef.current === 'land'
  const holdingReveal = phase === 'tarot_idle' && (enteringReveal || revealHolding)

  // Capture a content-area-centered rect for the card stage. Computed as soon as
  // the theater is active (shuffle onward) so the riffle, fan, and fly all share
  // one origin, then preserved through jiggle/flip/land. useLayoutEffect fires
  // before paint.
  useLayoutEffect(() => {
    if (
      phase !== 'shuffle'
      && phase !== 'pick_a_card'
      && phase !== 'flying'
      && phase !== 'jiggle'
      && phase !== 'flip'
    ) return
    if (capturedRect) return
    const isMobile = window.innerWidth < 1025
    const area = contentAreaRef?.current?.getBoundingClientRect()
    const areaTop = area?.top ?? 0
    const areaLeft = area?.left ?? 0
    const areaWidth = area?.width ?? window.innerWidth
    const areaHeight = area?.height ?? window.innerHeight
    const cardDims = getCardDimensions(areaWidth, isMobile, areaHeight)
    const jiggleScale = isMobile ? 0.7 : 1
    const cardWidth = cardDims.width * jiggleScale
    const cardHeight = cardDims.height * jiggleScale
    setCapturedRect({
      top: areaTop + (areaHeight - cardHeight) / 2,
      left: areaLeft + (areaWidth - cardWidth) / 2,
      width: cardWidth,
      height: cardHeight,
    })
  }, [phase, capturedRect, contentAreaRef])

  // Reset the captured rect when a reading is abandoned (any phase → idle). The
  // capture useLayoutEffect only runs for shuffle/pick/flying/jiggle/flip, so on
  // an existing-session boot (idle → tarot_idle) the rect is already null and
  // needs no reset here. After a live reading it's released when the reveal hold
  // ends (in the effect below), NOT on the land → tarot_idle edge — the held
  // overlay still needs it to clamp to the landing.
  useEffect(() => {
    if (phase === 'idle') setCapturedRect(null)
  }, [phase])

  // Drive the reveal hold: arm it on the land → tarot_idle edge and release both
  // the hold and the captured rect after REVEAL_OVERLAP_MS. Any phase that isn't
  // this reveal cancels it (e.g. a clarifying draw advancing tarot_idle →
  // pick_a_card mid-hold; the rect is dropped so that draw re-measures).
  useEffect(() => {
    const prev = prevPhaseRef.current
    prevPhaseRef.current = phase
    if (phase === 'tarot_idle' && prev === 'land') {
      setRevealHolding(true)
      const timer = global.setTimeout(() => {
        setRevealHolding(false)
        setCapturedRect(null)
      }, REVEAL_OVERLAP_MS)
      return () => global.clearTimeout(timer)
    }
    if (phase !== 'tarot_idle') {
      setRevealHolding(false)
      if (prev === 'tarot_idle') setCapturedRect(null)
    }
    return undefined
  }, [phase])

  const handleChooseCard = useCallback(() => {
    onChooseCard()
  }, [onChooseCard])

  // idle: render null
  if (phase === 'idle') {
    return null
  }

  // bad_question_ui: render null (the reframe modal is owned by the host/layout,
  // not this component).
  if (phase === 'bad_question_ui') {
    return null
  }

  // tarot_idle: normally render null — the structured card owns the screen. But
  // during the reveal hold, keep the stage mounted holding the landed card
  // (forced phase `land`) so it covers the structured card's fade-in. Once the
  // hold ends it unmounts invisibly (the structured card is solid by then).
  if (phase === 'tarot_idle' && !holdingReveal) {
    return null
  }

  // The unified rAF card stage (TarotCardStage) now owns EVERY animating phase:
  // the same persistent card elements riffle (shuffle), fan + pick (pick_a_card),
  // fly to center (flying), jiggle, flip, and land — with no unmount and no
  // CSS-keyframe seams. Because each phase's motion begins from the cards' live
  // transforms, every boundary is continuous by construction (AI-882 issues
  // 3/4/5). The chosen card flies to `capturedRect`, jiggles there, then flips
  // and flies to `[data-oracle-card-landing]`, holding as the AI-765 anti-flash
  // overlay through `land` until the structured card is revealed at tarot_idle.
  // The stage portals itself to document.body and runs its own image-decode
  // gate, so it mounts as soon as the rect + URL are known.
  // During the reveal hold the FSM is at tarot_idle but the overlay must keep
  // holding the landed pose — force `land` so the stage clamps on the landing.
  const stagePhase: TarotPhase = holdingReveal ? 'land' : phase

  return (
    capturedRect && backImageUrl ? (
      <TarotCardStage
        phase={stagePhase}
        backImageUrl={backImageUrl}
        frontImageUrl={frontImageUrl}
        jiggleTarget={capturedRect}
        onChooseCard={handleChooseCard}
        onFlipEnd={onFlipEnd}
      />
    ) : null
  )
}
